'use client';

import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

const BORDER_COLOR = '#D0D5DD';

export function isResourceDocumentPdfUrl(url: string, fileName?: string): boolean {
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  if (/\.pdf(\?|#|$)/i.test(trimmed)) return true;
  if (fileName && /\.pdf$/i.test(fileName.trim())) return true;
  return false;
}

export function buildPdfPreviewSrc(url: string): string {
  const base = url.trim().split('#')[0];
  return `${base}#toolbar=0&navpanes=0`;
}

function PreviewFallback({ message }: { message: string }) {
  return (
    <Typography
      sx={{
        px: 2,
        py: 2,
        textAlign: 'center',
        color: '#667085',
        fontSize: '0.875rem',
        fontFamily: 'Poppins, sans-serif',
        lineHeight: 1.5,
      }}
    >
      {message}
    </Typography>
  );
}

type Props = {
  downloadUrl: string | null;
  fileName?: string;
  fallbackMessage: string;
  /** When true, preview grows to fill a flex parent (e.g. brochure thank-you modal). */
  fillContainer?: boolean;
};

export default function ResourceDocumentPdfPreview({
  downloadUrl,
  fileName,
  fallbackMessage,
  fillContainer = false,
}: Props) {
  const [previewFailed, setPreviewFailed] = useState(false);
  const canPreview = Boolean(
    downloadUrl && isResourceDocumentPdfUrl(downloadUrl, fileName),
  );

  useEffect(() => {
    setPreviewFailed(false);
  }, [canPreview, downloadUrl]);

  if (!downloadUrl) {
    return null;
  }

  const showIframe = canPreview && !previewFailed;

  const frameSx = fillContainer
    ? {
        flex: '1 1 0',
        minHeight: 0,
        alignSelf: 'stretch',
        width: '100%',
        border: '1px solid',
        borderColor: BORDER_COLOR,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#F9FAFB',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        height: { xs: 260, sm: 380 },
        border: '1px solid',
        borderColor: BORDER_COLOR,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#F9FAFB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };

  return (
    <Box
      sx={
        fillContainer
          ? {
              flex: '1 1 0',
              minHeight: 0,
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : { width: '100%' }
      }
    >
      <Box
        sx={{
          ...frameSx,
          ...(!showIframe && fillContainer
            ? { alignItems: 'center', justifyContent: 'center' }
            : {}),
        }}
      >
        {showIframe ? (
          <Box
            component="iframe"
            src={buildPdfPreviewSrc(downloadUrl)}
            title="Document preview"
            onError={() => setPreviewFailed(true)}
            sx={{
              flex: fillContainer ? '1 1 0' : undefined,
              minHeight: fillContainer ? 0 : undefined,
              width: '100%',
              height: fillContainer ? '100%' : '100%',
              border: 0,
              display: 'block',
            }}
          />
        ) : (
          <PreviewFallback message={fallbackMessage} />
        )}
      </Box>
    </Box>
  );
}
